import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FilePdfOutlined,
  FileZipOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  DatePicker,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import { errorText, formatAmount, saveBlob } from '../format';

const { Text, Title } = Typography;

const LANGUAGES = ['sr', 'en', 'ru', 'zh'];
const AMOUNT_STYLE = { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
// Column headers of amounts may wrap; the amounts below them never do.
const HEAD_STYLE = { whiteSpace: 'normal' };
// Status tags one under another, each whole within the column.
const STATUS_STYLE = { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 };
const ddmmyyyy = (value) => (value ? dayjs(value).format('DD.MM.YYYY') : '');
const accountLabel = (a, t) =>
  [a.account, a.name, a.foreign ? `(${t('ios.foreign')})` : '']
    .filter(Boolean)
    .join(' ');

/**
 * Partners of one account on a date with the check against the books, a PDF
 * per partner and a ZIP for all of them.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company id
 * @param {string} props.asOf - "YYYY-MM-DD"
 * @param {string} props.account - Account
 * @param {string} props.language - '' = per partner, or sr / en / ru / zh
 * @param {boolean} [props.showZip] - Show the ZIP button above the table
 * @returns {JSX.Element} The table
 */
function IosPartnersTable({ companyId, asOf, account, language, showZip }) {
  const { t } = useTranslation('clientReports');
  const params = { asOf, account };
  const overviewQuery = useQuery({
    queryKey: ['client-reports-ios', companyId, params],
    queryFn: () => API.clientReports.getIosOverview(companyId, params),
    enabled: Boolean(asOf && account),
    retry: false,
  });
  const overview = overviewQuery.data?.data;

  const exportMutation = useMutation({
    mutationFn: ({ kind, partner }) =>
      API.clientReports.exportIos(companyId, kind, {
        ...params,
        partner,
        language: language || undefined,
      }),
    onSuccess: saveBlob,
    onError: async (e) => message.error(await errorText(e)),
  });

  // Amounts never wrap and line up digit under digit.
  const amountCell = (value) => (
    <span style={AMOUNT_STYLE}>{value == null ? '—' : formatAmount(value)}</span>
  );

  // Fixed layout: the table fits the screen width; only the partner's name
  // gives way (ellipsis, full name on hover).
  const columns = [
    { title: t('ios.partner'), dataIndex: 'partner', key: 'partner', width: 110, ellipsis: true },
    {
      title: t('ios.name'),
      dataIndex: 'partnerName',
      key: 'partnerName',
      ellipsis: { showTitle: false },
      render: (name) => (
        <Tooltip placement="topLeft" title={name}>
          {name}
        </Tooltip>
      ),
    },
    ...(overview?.account?.foreign
      ? [
          {
            title: <span style={HEAD_STYLE}>{t('ios.balanceFx')}</span>,
            key: 'fx',
            align: 'right',
            width: 140,
            render: (_, r) =>
              r.totals.map((x) =>
                x.currency ? (
                  <div key={x.currency} style={AMOUNT_STYLE}>
                    {formatAmount(x.balance)} {x.currency}
                  </div>
                ) : (
                  <Tag key="unknown" style={{ marginInlineEnd: 0 }}>
                    {t('ios.currencyUnknown')}
                  </Tag>
                )
              ),
          },
        ]
      : []),
    {
      title: <span style={HEAD_STYLE}>{t('ios.balanceIos')}</span>,
      dataIndex: 'iosBalance',
      key: 'iosBalance',
      align: 'right',
      width: 140,
      render: amountCell,
    },
    {
      title: <span style={HEAD_STYLE}>{t('ios.balanceBooks')}</span>,
      dataIndex: 'bookBalance',
      key: 'bookBalance',
      align: 'right',
      width: 140,
      render: amountCell,
    },
    {
      title: t('ios.status'),
      key: 'status',
      width: 170,
      render: (_, r) => (
        <div style={STATUS_STYLE}>
          {r.reconciled === true && <Tag color="green">{t('ios.reconciled')}</Tag>}
          {r.reconciled === false && (
            <Tooltip
              title={`${t('ios.difference', { amount: formatAmount(r.difference) })} · ${t('ios.unreconciledHint')}`}
            >
              <div>
                <Tag color="red">{t('ios.unreconciled')}</Tag>
                <div style={{ ...AMOUNT_STYLE, fontSize: 12, color: '#cf1322' }}>
                  {formatAmount(r.difference)}
                </div>
              </div>
            </Tooltip>
          )}
          {r.reconciled == null && (
            <Tooltip title={t('ios.noBooksHint')}>
              <Tag color="orange">{t('ios.noBooks')}</Tag>
            </Tooltip>
          )}
          {r.unbookedFxCount > 0 && (
            <Tooltip
              title={`${t('ios.unbookedFx', { count: r.unbookedFxCount })}. ${t('ios.unbookedFxHint')}`}
            >
              <Tag color="orange">
                {t('ios.unbookedFxShort', { count: r.unbookedFxCount })}
              </Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: t('ios.language'),
      dataIndex: 'defaultLanguage',
      key: 'defaultLanguage',
      width: 64,
      render: (lang) => (language || lang).toUpperCase(),
    },
    {
      key: 'actions',
      width: 84,
      render: (_, r) => (
        <Button
          size="small"
          shape="round"
          icon={<FilePdfOutlined />}
          loading={
            exportMutation.isPending &&
            exportMutation.variables?.partner === r.partner
          }
          onClick={() => exportMutation.mutate({ kind: 'pdf', partner: r.partner })}
        >
          PDF
        </Button>
      ),
    },
  ];

  const errorMessage = overviewQuery.error
    ? overviewQuery.error.response?.data?.message || overviewQuery.error.message
    : null;

  return (
    <div>
      <Space wrap style={{ display: 'flex', marginBottom: 8 }}>
        {showZip !== false && (
          <Button
            shape="round"
            icon={<FileZipOutlined />}
            disabled={!overview?.partners?.length}
            loading={exportMutation.isPending && exportMutation.variables?.kind === 'zip'}
            onClick={() => exportMutation.mutate({ kind: 'zip' })}
          >
            {t('ios.downloadAll')}
          </Button>
        )}
        {overview?.bookSource === 'ledger' && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('ios.booksFromLedger')}
          </Text>
        )}
      </Space>
      {errorMessage && (
        <Alert type="warning" showIcon message={errorMessage} style={{ marginBottom: 12 }} />
      )}
      {overview?.unreconciled > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('ios.unreconciledCount', { count: overview.unreconciled })}
        />
      )}
      <Table
        tableLayout="fixed"
        rowKey="partner"
        size="small"
        loading={overviewQuery.isLoading && overviewQuery.fetchStatus !== 'idle'}
        pagination={{ pageSize: 50, hideOnSinglePage: true }}
        columns={columns}
        dataSource={overview?.partners || []}
      />
    </div>
  );
}

/**
 * IOS (izvod otvorenih stavki) for HLB's accountants: the open items AB
 * holds for a customer or supplier account on a date, partner by partner,
 * checked against the partner's balance in the books. Any difference, also
 * 0,01, flags the partner — the items have to be closed in AB; nothing is
 * corrected here. Accounts AB's functions returned outside the IOS rules
 * (neither 20x nor 43x) are shown in a block of their own, never dropped.
 * The PDFs go to the client only as a file of a published pack.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company whose open items are shown
 * @returns {JSX.Element} The panel
 */
export default function IosPanel({ companyId }) {
  const { t } = useTranslation('clientReports');
  const queryClient = useQueryClient();
  const [asOf, setAsOf] = useState(null);
  const [account, setAccount] = useState(null);
  const [language, setLanguage] = useState('');
  const [requestDate, setRequestDate] = useState(null);

  const datesQuery = useQuery({
    queryKey: ['client-reports-ios-dates', companyId],
    queryFn: () => API.clientReports.getIosDates(companyId),
  });
  const snapshots = useMemo(
    () => datesQuery.data?.data?.snapshots || [],
    [datesQuery.data]
  );
  const pending = datesQuery.data?.data?.pending || [];
  const snapshot = snapshots.find((s) => s.asOf === asOf) || null;
  // Accounts of the date with how many partners each has and how many of them
  // do not agree with the books.
  const accountsQuery = useQuery({
    queryKey: ['client-reports-ios-accounts', companyId, asOf],
    queryFn: () => API.clientReports.getIosAccounts(companyId, asOf),
    enabled: Boolean(asOf),
    retry: false,
  });
  const allAccounts = useMemo(
    () => accountsQuery.data?.data?.accounts || snapshot?.accounts || [],
    [accountsQuery.data, snapshot]
  );
  const accounts = useMemo(
    () => allAccounts.filter((a) => !a.outsideRules),
    [allAccounts]
  );
  const outsideAccounts = useMemo(
    () => allAccounts.filter((a) => a.outsideRules),
    [allAccounts]
  );
  const countedLabel = (a) => {
    if (a.partnerCount == null) return accountLabel(a, t);
    const base = `${accountLabel(a, t)} · ${a.partnerCount}`;
    return a.unreconciled
      ? `${base} (${t('ios.accountUnreconciled', { count: a.unreconciled })})`
      : base;
  };

  // A new company starts on its newest date and first account.
  useEffect(() => {
    setAsOf(null);
    setAccount(null);
  }, [companyId]);
  useEffect(() => {
    if (!asOf && snapshots.length) setAsOf(snapshots[0].asOf);
  }, [snapshots, asOf]);
  // Open the first account that has partners, not an empty one; wait for the
  // counts so the choice is made on them.
  useEffect(() => {
    if (!accountsQuery.data || !accounts.length) return;
    if (!accounts.some((a) => a.account === account)) {
      const withPartners = accounts.find((a) => a.partnerCount > 0);
      setAccount((withPartners || accounts[0]).account);
    }
  }, [accounts, account, accountsQuery.data]);

  const requestMutation = useMutation({
    mutationFn: (date) => API.clientReports.requestIosDate(companyId, date),
    onSuccess: () => {
      message.success(t('ios.requested'));
      setRequestDate(null);
      queryClient.invalidateQueries({
        queryKey: ['client-reports-ios-dates', companyId],
      });
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  const languageOptions = [
    { value: '', label: t('ios.languageAuto') },
    ...LANGUAGES.map((l) => ({
      value: l,
      label:
        t(`ios.languages.${l}`) +
        (['ru', 'zh'].includes(l) ? ` (${t('ios.draft')})` : ''),
    })),
  ];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={t('ios.hint')}
      />

      <Space wrap style={{ display: 'flex', marginBottom: 12 }}>
        <Text>{t('ios.date')}</Text>
        <Select
          style={{ width: 270 }}
          value={asOf}
          onChange={setAsOf}
          placeholder={t('ios.noDates')}
          popupMatchSelectWidth={false}
          options={snapshots.map((s) => ({
            value: s.asOf,
            label: `${ddmmyyyy(s.asOf)} · ${t('ios.takenAt', {
              when: dayjs(s.receivedAt).format('DD.MM. HH:mm'),
            })}`,
          }))}
        />
        <Text>{t('ios.account')}</Text>
        <Select
          style={{ minWidth: 360 }}
          popupMatchSelectWidth={false}
          value={account}
          onChange={setAccount}
          disabled={!accounts.length}
          loading={accountsQuery.isLoading}
          options={accounts.map((a) => ({ value: a.account, label: countedLabel(a) }))}
        />
        <Text>{t('ios.language')}</Text>
        <Select
          style={{ width: 190 }}
          value={language}
          onChange={setLanguage}
          options={languageOptions}
        />
      </Space>

      <Space wrap style={{ display: 'flex', marginBottom: 12 }}>
        <Text type="secondary">{t('ios.requestLabel')}</Text>
        <DatePicker
          value={requestDate}
          format="DD.MM.YYYY"
          onChange={setRequestDate}
          disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
        />
        <Button
          shape="round"
          icon={<SendOutlined />}
          disabled={!requestDate}
          loading={requestMutation.isPending}
          onClick={() => requestMutation.mutate(requestDate.format('YYYY-MM-DD'))}
        >
          {t('ios.request')}
        </Button>
        {pending.length > 0 && (
          <Text type="secondary">
            {t('ios.pending', { dates: pending.map(ddmmyyyy).join(', ') })}
          </Text>
        )}
      </Space>

      {!datesQuery.isLoading && !snapshots.length && (
        <Alert type="warning" showIcon message={t('ios.noDates')} style={{ marginBottom: 12 }} />
      )}
      {snapshot && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
          {t('ios.received', {
            received: dayjs(snapshot.receivedAt).format('DD.MM.YYYY HH:mm'),
            source: snapshot.source || '—',
          })}
        </Text>
      )}

      {asOf && account && (
        <IosPartnersTable
          companyId={companyId}
          asOf={asOf}
          account={account}
          language={language}
        />
      )}

      {asOf && outsideAccounts.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Title level={5}>{t('ios.outsideTitle')}</Title>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={t('ios.outsideHint')}
          />
          {outsideAccounts.map((a) => (
            <div key={a.account} style={{ marginBottom: 16 }}>
              <Text strong>{countedLabel(a)}</Text>
              <IosPartnersTable
                companyId={companyId}
                asOf={asOf}
                account={a.account}
                language={language}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
